import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { articles } from '@/mocks/articles';
import type { Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const found = articles.find((a) => a.id === id);
    setArticle(found || null);
    if (found) {
      setRelatedArticles(articles.filter((a) => a.id !== id).slice(0, 3));
    }
  }, [id]);

  useEffect(() => {
    if (!article) return;

    const existing = document.getElementById('schema-article');
    if (existing) existing.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: { '@type': 'Organization', name: article.author },
      datePublished: article.publishDate,
      url: `https://www.91piianku.com/article/${article.id}`,
      publisher: { '@type': 'Organization', name: '91片库', url: 'https://www.91piianku.com' },
      inLanguage: 'zh-CN',
    };

    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91piianku.com' },
        { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.91piianku.com/category/dianying` },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    };

    const script1 = document.createElement('script');
    script1.id = 'schema-article';
    script1.type = 'application/ld+json';
    script1.textContent = JSON.stringify(schema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.id = 'schema-breadcrumb-article';
    script2.type = 'application/ld+json';
    script2.textContent = JSON.stringify(breadcrumb);
    document.head.appendChild(script2);

    return () => {
      const el1 = document.getElementById('schema-article');
      if (el1) el1.remove();
      const el2 = document.getElementById('schema-breadcrumb-article');
      if (el2) el2.remove();
    };
  }, [article]);

  if (!article) {
    return (
      <main className="min-h-screen pt-20 pb-16 bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-article-line text-5xl text-foreground-500 mb-4 block"></i>
          <p className="text-foreground-300 text-lg">文章不存在</p>
          <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer">
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-20 pb-16 bg-background-50">
      <article className="max-w-[900px] mx-auto px-4 md:px-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
          <span className="text-foreground-700">/</span>
          <span className="text-foreground-400">{article.category}</span>
          <span className="text-foreground-700">/</span>
          <span className="text-foreground-300 line-clamp-1">{article.title}</span>
        </nav>

        <header className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="px-2.5 py-1 text-xs bg-primary-500/10 text-primary-500 rounded-full whitespace-nowrap">
              {article.category}
            </span>
            {article.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 text-xs bg-background-100 text-foreground-500 border border-background-200/40 rounded-full whitespace-nowrap">
                {tag}
              </span>
            ))}
          </div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-50 leading-tight mb-4">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              {article.readTime}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
              {article.views.toLocaleString()} 阅读
            </span>
          </div>
        </header>

        <div className="mb-10 rounded-xl overflow-hidden">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 91片库影视资讯`}
            className="w-full aspect-[2/1] object-cover"
          />
        </div>

        <div
          className="prose-custom text-foreground-300 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        <div className="mt-12 pt-8 border-t border-background-200/40">
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/category/dianying`}
                className="px-3 py-1.5 text-xs bg-background-100 border border-background-200/40 rounded-full text-foreground-500 hover:text-primary-500 hover:border-primary-500/30 transition-colors whitespace-nowrap cursor-pointer"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <section className="mt-12 pt-8 border-t border-background-200/40">
            <h3 className="text-lg font-heading font-semibold text-foreground-100 mb-6">相关文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group bg-background-100 border border-background-200/40 rounded-lg overflow-hidden hover:border-primary-500/30 transition-all cursor-pointer"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={ra.cover}
                      alt={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-2">
                      {ra.title}
                    </h4>
                    <p className="text-xs text-foreground-600 mt-1">{ra.publishDate}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}